import { FocusEvent } from 'react';
import { BadgeIcon, Button, Dialog, Group, Input, Tooltip } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useField, useForm } from 'react-final-form';
import { Field } from '~/app/shared/components/Form';
import { BankAccountDropdown } from '~/app/shared/components/ui/BankAccountDropdown';
import AccountCombobox from '~/app/shared/components/ui/AccountCombobox';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { AccountModel, AccountTypeNumber } from '~/types';
import { CollectionQuery } from '~/app/shared/hooks/useCollectionQuery';
import { AddModal } from '~/app/pages/accounting/bank/accounts/components/modals/AddModal';
import useCachedQuery from '~/app/shared/hooks/useCachedQuery';
import { BankDto, PaymentAccountWithIntegrationCurrencyDto } from '~/types/internal-api';
import useQuery from '~/app/shared/hooks/useQuery';
import { Account } from '~/app/pages/accounting/bank/accounts/types';
import { addBankAccount, mapBankAccount } from '~/app/pages/accounting/bank/accounts/utils';
import PaymentLinkJournalDropdown from '../PaymentLinkJournalDropdown';

export const validateReceivableAccount = async (value: number | string) => {
    if (!value) {
        return t`Receivable account is required`;
    }

    if (isNaN(value as number)) {
        return t`Receivable account is not a valid balance account`;
    }

    const res = await internalApiClient.get<CollectionQuery<AccountModel>>(
        `/accounts/collection?filter=typeid$eq:${AccountTypeNumber.BalanceSheet}&returnAll=true`
    );

    const account = res.data.data.find(account => account.number === Number(value));

    return account ? undefined : t`Receivable account is not a valid balance account`;
};

export const validateFeeAccount = async (value: number | string) => {
    if (!value) {
        return t`Fee account is required`;
    }

    if (isNaN(value as number)) {
        return t`Fee account is not a valid profit and loss account`;
    }

    const res = await internalApiClient.get<CollectionQuery<AccountModel>>(
        `/accounts/collection?filter=typeid$eq:${AccountTypeNumber.ProfitAndLoss}&returnAll=true`
    );

    const account = res.data.data.find(account => account.number === Number(value));

    return account ? undefined : t`Fee account is not a valid profit and loss account`;
};

const validatePaymentAccount = (value: string | null) => (value ? undefined : t`Bank account is required`);

const validateIban = (value: string) =>
    /^([a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16})$/g.test(value) ? undefined : t`IBAN number is required`;

export const onComboboxBlur = (
    eventOrValue: FocusEvent<HTMLInputElement> | number | string,
    field: {
        input: {
            onChange: (value: FocusEvent<HTMLInputElement> | number | string) => void;
            onBlur: () => void;
        };
    }
) => {
    const value = (eventOrValue as FocusEvent<HTMLInputElement>).target?.value || eventOrValue;
    const numberValue = isNaN(value as number) ? value : Number(value);

    field.input.onChange(numberValue);
    field.input.onBlur();
};

interface Props {
    prevStep: () => void;
    refetchBankAccounts: () => Promise<void>;
}

const PaymentAndAccountingSettings = ({ prevStep, refetchBankAccounts }: Props) => {
    const { submit } = useForm();
    const fieldPaymentAccount = useField('paymentAccount');
    const fieldReceivableAccount = useField('receivableAccount');
    const fieldBankIban = useField('bankIban');
    const fieldFeeAccount = useField('feeAccount');

    const { data: banks } = useCachedQuery<BankDto[]>(`/smartbank/banks-collection`, []);
    const { data: paymentAccounts } = useQuery<PaymentAccountWithIntegrationCurrencyDto[]>(
        '/paymentAccounts/accounts-with-integration-currency',
        []
    );

    const onPrevClick = () => {
        prevStep();
    };

    const onNextClick = () => {
        fieldPaymentAccount.input.onBlur();
        fieldReceivableAccount.input.onBlur();
        fieldBankIban.input.onBlur();
        fieldFeeAccount.input.onBlur();

        if (
            fieldPaymentAccount.meta.invalid ||
            fieldReceivableAccount.meta.invalid ||
            fieldBankIban.meta.invalid ||
            fieldFeeAccount.meta.invalid
        ) {
            return;
        }

        // Invokes handleSubmit() from index.tsx
        submit();
    };

    const addAccount = async (account: Account) => {
        const newAccount = await addBankAccount(account);
        const { number } = newAccount;
        await refetchBankAccounts();

        fieldPaymentAccount.input.onChange(number);
    };

    return (
        <>
            <Dialog.Title>
                <Trans>Payment and accounting settings</Trans>
            </Dialog.Title>

            <div className="flex mt-4">
                <Trans>Choose settings for customer payments via the payment link below</Trans>
            </div>

            <div className="flex mt-6">
                <Field
                    required
                    fieldClassName="w-full"
                    name="paymentAccount"
                    label={t`Your bank account`}
                    as={BankAccountDropdown}
                    validateFields={[]}
                    validate={validatePaymentAccount}
                />
            </div>

            <div className="flex justify-end">
                <Button
                    appearance="discrete"
                    dialog={dialogProps => (
                        <AddModal
                            onClose={() => {}}
                            addAccount={addAccount}
                            accounts={mapBankAccount(paymentAccounts)}
                            banks={banks}
                            skipNotifications
                            autoclose
                            {...dialogProps}
                        />
                    )}>
                    <Trans>Add bank account</Trans>
                </Button>
            </div>

            <div className="flex mt-2">
                <Field
                    required
                    fieldClassName="w-full"
                    label={t`IBAN of your bank account`}
                    name="bankIban"
                    validateFields={[]}
                    validate={validateIban}>
                    {({ input, meta: { touched, error } }) => (
                        <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                            <span className="flex items-center">
                                <Trans>IBAN of your bank account</Trans> *
                                <Tooltip
                                    title={t`The IBAN is required for you to receive payments in all currencies. You can find the IBAN in your online banking. Please make sure to select the same account that you chose above.`}>
                                    <BadgeIcon small icon="info" color="blue" className="ml-1" />
                                </Tooltip>
                            </span>
                            <Input invalid={error && touched} {...input} />
                            {error && touched && (
                                <span className="h-4 text-xs text-left leading-normal font-normal truncate -mb-4 text-red-500">
                                    {error}
                                </span>
                            )}
                        </label>
                    )}
                </Field>
            </div>

            <div className="flex mt-3">
                <Field
                    required
                    fieldClassName="w-full"
                    label={t`Receivable account for pensopay payouts`}
                    name="receivableAccount"
                    validateFields={[]}
                    validate={validateReceivableAccount}>
                    {({ input, meta: { touched, error } }) => (
                        <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                            <span className="flex items-center">
                                <Trans>Receivable account for pensopay payouts</Trans> *
                                <Tooltip
                                    title={t`When your customer pays the invoice, the money will first be held by pensopay before being transferred to your account. Therefore, you need to set up an account where this is posted. You can select an existing account, but we recommend that you create a new, VAT-free debit account in your balance sheet.`}>
                                    <BadgeIcon small icon="info" color="blue" className="ml-1" />
                                </Tooltip>
                            </span>
                            <AccountCombobox
                                {...input}
                                isBlockedForDirectEntries={false}
                                onBlur={eventOrValue => onComboboxBlur(eventOrValue, fieldReceivableAccount)}
                                invalid={touched && error}
                            />
                            {error && touched && (
                                <span className="h-4 text-xs text-left leading-normal font-normal truncate -mb-4 text-red-500">
                                    {error}
                                </span>
                            )}
                        </label>
                    )}
                </Field>
            </div>

            <div className="flex mt-3">
                <Field
                    required
                    fieldClassName="w-full"
                    name="feeAccount"
                    label={t`Account for fees`}
                    validateFields={[]}
                    validate={validateFeeAccount}>
                    {({ input, meta: { touched, error } }) => (
                        <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                            <span className="flex items-center">
                                <Trans>Account for fees</Trans> *
                                <Tooltip
                                    title={t`Select or create an account to book fees from pensopay. We recommend using a profit and loss account without VAT.`}>
                                    <BadgeIcon small icon="info" color="blue" className="ml-1" />
                                </Tooltip>
                            </span>
                            <AccountCombobox
                                isBlockedForDirectEntries={false}
                                {...input}
                                invalid={touched && error}
                                onBlur={eventOrValue => onComboboxBlur(eventOrValue, fieldFeeAccount)}
                            />
                            {error && touched && (
                                <span className="h-4 text-xs text-left leading-normal font-normal truncate -mb-4 text-red-500">
                                    {error}
                                </span>
                            )}
                        </label>
                    )}
                </Field>
            </div>

            <div className="flex mt-3">
                <Field fieldClassName="w-full" name="daybook" validateFields={[]}>
                    {({ input }) => (
                        <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                            <span className="flex items-center">
                                <Trans>Journal for pensopay entries</Trans>
                                <Tooltip title={t`All entry types must be allowed in the selected journal`}>
                                    <BadgeIcon small icon="info" color="blue" className="ml-1" />
                                </Tooltip>
                            </span>
                            <PaymentLinkJournalDropdown {...input} />
                        </label>
                    )}
                </Field>
            </div>

            <Group className="flex mt-4 justify-end">
                <Button onClick={onPrevClick}>
                    <Trans>Back</Trans>
                </Button>
                <Button appearance="primary" onClick={onNextClick}>
                    <Trans>Next</Trans>
                </Button>
            </Group>
        </>
    );
};

export default PaymentAndAccountingSettings;
